import { useMemo, useState } from "react";
import { useApp } from "../../context/AppContext";
import { markOpeningSeen } from "../../storage/session";
import { EncryptedPhoto } from "../shared/EncryptedPhoto";

interface HeritageBeat {
  id: string;
  kicker: string;
  headline: string;
  body: string;
  preferredStem: string;
  fallbackIndex: number;
}

const BEATS: HeritageBeat[] = [
  {
    id: "dance",
    kicker: "Hampton Beach • Summer 1953",
    headline: "Once upon a time",
    body: "On a starry night, a dapper young Navy man named Bob asked a beautiful, confident nurse named Cathie to dance. Love at first sight — the Foxtrot led to a Waltz, and on to swing-time.",
    preferredStem: "sundius-wedding-couple",
    fallbackIndex: 0,
  },
  {
    id: "pie",
    kicker: "Before the night was through",
    headline: "The pie sealed the deal",
    body: "Cathie invited Bob home for homemade pie. One slice, and Bob knew this was the girl he was going to marry. (Small detail — Cathie's mom actually made the pie.)",
    preferredStem: "sundius-wedding-portrait",
    fallbackIndex: 3,
  },
  {
    id: "ring",
    kicker: "A fairytale, told again and again",
    headline: "A lucky horse & a dream wedding",
    body: "Bob put down what little money he had on a lucky horse — and won just enough for an engagement ring. Cathie, luckier still, won the Grand Prize at a local bridal show: a dream wedding, everything included, from a hand-sewn silk gown to a reception with double cascading staircases.",
    preferredStem: "sundius-wedding-dress",
    fallbackIndex: 2,
  },
  {
    id: "pop-pop",
    kicker: "Pop Pop",
    headline: "The dapper Navy man",
    body: "Robert Edwin Sundius — born in New York, degrees from Penn State and Maryland. He served aboard the USS Saratoga and taught electrical engineering at the U.S. Naval Academy, where several of his children would later be born. He painted in watercolor and oils.",
    preferredStem: "sundius-sailor-pop-pop",
    fallbackIndex: 4,
  },
  {
    id: "grammie",
    kicker: "Grammie",
    headline: "The nurse from Manchester",
    body: "Catherine Margaret Thompson — born May 12, 1926 in Manchester, New Hampshire, the youngest of eight Thompson siblings. A nurse, a mother of nine, and the heart of this family for generations.",
    preferredStem: "cathy-thompson-nurse",
    fallbackIndex: 0,
  },
  {
    id: "nine",
    kicker: "Nine lucky kids",
    headline: "Nine children, one story",
    body: "Mary Ellen, Jane, Bob, Kathleen, Carl, Peggy, Dan, Ann, and Mike — born between Albuquerque and Syracuse. Nine siblings, 24 grandchildren, and the cousins you know today.",
    preferredStem: "sundius-young-family",
    fallbackIndex: 5,
  },
  {
    id: "legacy",
    kicker: "Nearly six decades later",
    headline: "“He’s the love of my life.”",
    body: "Six decades of huge family holidays, changing addresses, laughter-filled vacations, and yummy pies actually made by Cathie. Even when Grammie's memories faded, she still heard Bob — she'd point at him, sure of her recipe for happiness.",
    preferredStem: "sundius-all-grandkids",
    fallbackIndex: 7,
  },
];

function WelcomeBeat({ onContinue }: { onContinue: () => void }) {
  const { bundle } = useApp();
  const gallery = bundle?.grandparent_gallery ?? [];
  const preferred = ["sundius-wedding-couple", "sundius-wedding-portrait", "sundius-wedding-staircase"];
  const stem =
    preferred.find((s) => gallery.some((g) => g.stem === s)) ??
    bundle?.meta.opening_photo ??
    preferred[0];

  return (
    <div className="heritage-full">
      <div className="heritage-welcome">
        <p className="heritage-whisper">Welcome to the</p>
        <h1 className="heritage-brand">Sundius</h1>
        <p className="heritage-whisper">family app</p>
        <div className="heritage-hero-frame">
          <EncryptedPhoto
            stem={stem}
            alt="Pop Pop & Grammie"
            className="heritage-hero-photo"
            fit="cover"
          />
        </div>
        <p className="heritage-caption">Pop Pop & Grammie</p>
        <button type="button" className="btn-primary" onClick={onContinue}>
          Continue
        </button>
      </div>
    </div>
  );
}

function HeritageStory({
  onComplete,
  onJumpTree,
}: {
  onComplete: () => void;
  onJumpTree: () => void;
}) {
  const { bundle } = useApp();
  const gallery = bundle?.grandparent_gallery ?? [];
  const [page, setPage] = useState(0);
  const isFinale = page >= BEATS.length;

  const stemFor = (beat: HeritageBeat) => {
    if (gallery.some((g) => g.stem === beat.preferredStem)) return beat.preferredStem;
    if (gallery.length > 0) {
      return gallery[beat.fallbackIndex % gallery.length]?.stem ?? beat.preferredStem;
    }
    return beat.preferredStem;
  };

  if (isFinale) {
    return (
      <div className="heritage-full">
        <div className="heritage-welcome">
          <p className="heritage-whisper">Your family</p>
          <h1 className="title-serif heritage-finale-title">
            The Sundius Family
          </h1>
          <p className="heritage-caption" style={{ textAlign: "center" }}>
            Explore the tree, map every cousin, play the games, and keep the story going.
          </p>
          <div className="heritage-gallery-grid">
            {gallery.slice(0, 6).map((photo) => (
              <figure key={photo.stem} className="heritage-gallery-card">
                <EncryptedPhoto
                  stem={photo.stem}
                  alt={photo.label}
                  className="heritage-gallery-photo"
                  fit="cover"
                />
                <figcaption>{photo.label}</figcaption>
              </figure>
            ))}
          </div>
          <button type="button" className="btn-primary" onClick={onComplete}>
            Enter the app
          </button>
          <button type="button" className="btn-ghost" onClick={onJumpTree}>
            Jump to the tree
          </button>
        </div>
      </div>
    );
  }

  const beat = BEATS[page]!;
  const stem = stemFor(beat);

  return (
    <div className="heritage-full">
      <div className="heritage-story">
        <div className="heritage-progress">
          {BEATS.map((b, i) => (
            <span key={b.id} className={i <= page ? "active" : ""} />
          ))}
        </div>
        <div className="heritage-beat-card">
          <EncryptedPhoto
            stem={stem}
            alt={beat.headline}
            className="heritage-beat-photo"
            fit="cover"
          />
          <div className="heritage-beat-copy">
            <p className="heritage-kicker">{beat.kicker}</p>
            <h2 className="title-serif heritage-beat-headline">{beat.headline}</h2>
            <p>{beat.body}</p>
          </div>
        </div>
        <div className="heritage-nav">
          <button
            type="button"
            className="btn-ghost"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            Back
          </button>
          <button type="button" className="btn-primary" onClick={() => setPage((p) => p + 1)}>
            {page === BEATS.length - 1 ? "Continue" : "Next"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function HeritageIntro() {
  const { showHeritage, setShowHeritage, bundle, setSelectedPersonId, setActiveTab, setFamilyBrowseMode } =
    useApp();
  const [stage, setStage] = useState<"welcome" | "heritage">("welcome");

  const siblingRoot = useMemo(
    () => bundle?.meta.root_ids[0] ?? null,
    [bundle],
  );

  if (!showHeritage || !bundle) return null;

  const finish = () => {
    markOpeningSeen();
    setShowHeritage(false);
    setStage("welcome");
  };

  const jumpTree = () => {
    markOpeningSeen();
    setShowHeritage(false);
    setStage("welcome");
    setActiveTab("family");
    setFamilyBrowseMode("tree");
    if (siblingRoot) setSelectedPersonId(siblingRoot);
  };

  return (
    <div className="overlay heritage-overlay">
      {stage === "welcome" ? (
        <WelcomeBeat onContinue={() => setStage("heritage")} />
      ) : (
        <HeritageStory onComplete={finish} onJumpTree={jumpTree} />
      )}
    </div>
  );
}

export function TabIntro({ tab }: { tab: string }) {
  const copy: Record<string, string> = {
    home: "Your personalized home base — quick jumps, birthdays, and family moments.",
    family: "Browse the tree, map, and face directory.",
    play: "Flashcards, birth order, trivia, and the shared leaderboard.",
    news: "Birthdays, announcements, and events — synced with everyone.",
  };
  return <>{copy[tab]}</>;
}
